import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import API from '@/axios/API';
import PropTypes from 'prop-types';
import { DataContext } from '../DataProviders/DataProvider';

const SequencerContext = createContext();

export const SequencerProvider = ({ children, customerId }) => {
    const { loginCustomerId: contextCustomerId } = useContext(DataContext);
    const loginCustomerId = customerId || contextCustomerId;

    // Sequencer data state
    const [sequencers, setSequencers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Stats state
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        connected: 0,
    });

    // Fetch sequencers from API
    const fetchSequencers = useCallback(async () => {
        console.log(typeof loginCustomerId)
        if (loginCustomerId === "null" || !loginCustomerId) {
            setLoading(false)
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Fetch sequencers list
            const sequencersPromise = API.get(
                `${import.meta.env.BACKEND_API}/api/clientsequencer/${loginCustomerId}`
            );

            // Fetch stats
            const statsPromise = API.get(
                `${import.meta.env.BACKEND_API}/api/sequencercount?customerId=${loginCustomerId}`
            );

            const [sequencersResponse, statsResponse] = await Promise.all([
                sequencersPromise,
                statsPromise
            ]);

            // Handle sequencers response
            if (sequencersResponse.data?.success && Array.isArray(sequencersResponse.data.items)) {
                setSequencers(sequencersResponse.data.items);
            } else {
                setSequencers([]);
            }

            // Handle stats response
            if (statsResponse.data) {
                setStats({
                    total: statsResponse.data.totalCount || 0,
                    active: statsResponse.data.activeCount || 0,
                    connected: statsResponse.data.connectedCount || 0,
                });
            }

        } catch (err) {
            console.error('Error fetching data:', err);
            setError(err.message || 'Failed to fetch data');
            // Don't clear sequencers on error if we simply failed to refresh
            // But if it's initial load, we might want to ensure empty state
            // Keeping existing behavior for now but modified for safety
            if (sequencers.length === 0) setSequencers([]);
        } finally {
            setLoading(false);
        }
    }, [loginCustomerId]);

    // Fetch sequencers when component mounts or loginCustomerId changes
    useEffect(() => {
        fetchSequencers();
    }, [fetchSequencers]);

    const value = {
        sequencers,
        loading,
        error,
        stats,
        activeCustomerId: loginCustomerId,
        refetch: fetchSequencers,
    };

    return (
        <SequencerContext.Provider value={value}>
            {children}
        </SequencerContext.Provider>
    );
};

SequencerProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useSequencer = () => {
    const context = useContext(SequencerContext);
    if (!context) {
        throw new Error('useSequencer must be used within a SequencerProvider');
    }
    return context;
};

